/**
 * Single point of contact with posthog-js. PostHog is only initialised after
 * explicit cookie consent (RGPD) — every helper is a no-op before that.
 */
import posthog from 'posthog-js'
import type { Subscriber } from '@/features/auth/types'

const CONSENT_KEY = 'alerte-immo:consent'

export type ConsentStatus = 'accepted' | 'refused'

export function getConsent(): ConsentStatus | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY)
    return value === 'accepted' || value === 'refused' ? value : null
  } catch {
    return null
  }
}

export function setConsent(status: ConsentStatus): void {
  try {
    localStorage.setItem(CONSENT_KEY, status)
  } catch {
    // localStorage unavailable — consent simply won't persist.
  }
  setGoogleConsent(status === 'accepted')
  if (status === 'accepted') {
    // Re-consent after a withdrawal: PostHog is already loaded, just opt back in.
    if (initialized) posthog.opt_in_capturing()
    else initAnalytics()
  } else if (initialized) {
    // Withdrawal (CNIL): stop capture and drop the PostHog identity/cookies.
    posthog.opt_out_capturing()
    posthog.reset()
  }
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT))
}

const CONSENT_CHANGED_EVENT = 'alerte-immo:consent-changed'

/** Notifie les consommateurs du consentement (ex. chargement du chatbot). */
export function onConsentChanged(handler: () => void): () => void {
  window.addEventListener(CONSENT_CHANGED_EVENT, handler)
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handler)
}

const OPEN_CONSENT_EVENT = 'alerte-immo:open-cookie-consent'

/** Rouvre la bannière de consentement (lien "Gérer les cookies"). */
export function openCookieConsent(): void {
  window.dispatchEvent(new Event(OPEN_CONSENT_EVENT))
}

export function onOpenCookieConsent(handler: () => void): () => void {
  window.addEventListener(OPEN_CONSENT_EVENT, handler)
  return () => window.removeEventListener(OPEN_CONSENT_EVENT, handler)
}

let initialized = false

export function initAnalytics(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined
  if (initialized || !key || getConsent() !== 'accepted') return
  posthog.init(key, {
    api_host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://eu.i.posthog.com',
    defaults: '2025-05-24',
    session_recording: { maskAllInputs: true },
    // Le lien invité /app/decouverte?token=… porte un token signé : le $pageview
    // initial part avant que la page ait strippé l'URL. On masque le token de
    // toute propriété d'URL pour qu'il n'atteigne jamais PostHog.
    sanitize_properties: (properties) => {
      for (const key of [
        '$current_url',
        '$referrer',
        '$pathname',
        '$initial_current_url',
        '$initial_referrer',
      ]) {
        const value = properties[key]
        if (typeof value === 'string' && /[?&]token=/.test(value)) {
          properties[key] = value.replace(/([?&])token=[^&#]*/g, '$1token=***')
        }
      }
      return properties
    },
  })
  initialized = true
}

export function identifyUser(user: Subscriber): void {
  if (!initialized) return
  posthog.identify(user.id, {
    email: user.email,
    status: user.status,
    role: user.role ?? 'user',
  })
}

export function resetAnalytics(): void {
  if (initialized) posthog.reset()
}

export function capture(event: string, properties?: Record<string, unknown>): void {
  if (initialized) posthog.capture(event, properties)
}

const GADS_ID = 'AW-18283245018'

type Gtag = (...args: unknown[]) => void
function getGtag(): Gtag | undefined {
  return (window as unknown as { gtag?: Gtag }).gtag
}

/** Google Ads (Consent Mode v2) : bascule les 4 signaux selon le consentement. */
function setGoogleConsent(granted: boolean): void {
  const g = getGtag()
  if (!g) return
  const v = granted ? 'granted' : 'denied'
  g('consent', 'update', {
    ad_storage: v,
    ad_user_data: v,
    ad_personalization: v,
    analytics_storage: v,
  })
}

/**
 * Google Ads — conversion d'inscription, déclenchée à la création de compte
 * réussie (SignupPage). Le snippet "Purchase" est appliqué tel quel ; le
 * Consent Mode décide si le hit pose un cookie (accord) ou reste modélisé.
 */
export function trackSignupConversion(): void {
  const g = getGtag()
  if (!g) return
  g('event', 'conversion', {
    send_to: `${GADS_ID}/sXLbCK-14MgcENrbkI5E`,
    value: 1.0,
    currency: 'EUR',
    transaction_id: '',
  })
}

export { posthog }
